import React, { useState, useEffect } from 'react';
import { StarPolygonIcon, RobotIcon, CreditCardIcon, DiamondIcon, CheckIcon, CopyIcon } from './Icons';

const PaymentModalView = ({
    domain,
    action, // 'buy', 'rent', 'forward'
    durationText, // e.g., '1 month', '15 days', 'Permanent purchase'
    amount, // numeric amount in USD
    invoiceData,
    telegramBotUsername = '',
    destination = null,
    onBack
}) => {
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [selectedNetKey, setSelectedNetKey] = useState(null);
    const [copied, setCopied] = useState(false);
    

    useEffect(() => {
        fetch('/api/payment-config')
            .then(res => res.json())
            .then(data => {
                if (data.success) {
                    setPaymentConfig(data);
                    const walletKeys = Object.keys(data.wallets || {});
                    if (walletKeys.length > 0) {
                        setSelectedNetKey(walletKeys[0]);
                    }
                }
            })
            .catch(err => console.error('Failed to load payment configuration:', err));
    }, []);

    const botUsername = paymentConfig?.telegramBotUsername || telegramBotUsername || process.env.REACT_APP_TELEGRAM_BOT_USERNAME || process.env.TELEGRAM_BOT_USERNAME || '';
    const wallets = paymentConfig?.wallets || {};
    const walletKeys = Object.keys(wallets);
    const hasDirectWallets = walletKeys.length > 0;
    const hasCryptoBot = !!(paymentConfig?.hasCryptoBot && invoiceData?.payUrl);

    const handleCopy = (address) => {
        if (!address) return;
        navigator.clipboard.writeText(address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const currentWallet = selectedNetKey ? wallets[selectedNetKey] : null;

    // Deep link generation
    let deepLink = `https://t.me/${botUsername}?start=domain_${domain?.id}`;
    if (action === 'buy') {
        deepLink = `https://t.me/${botUsername}?start=buy_${domain?.id}`;
    } else if (action === 'rent') {
        const months = invoiceData?.duration || 1;
        deepLink = `https://t.me/${botUsername}?start=rent_${domain?.id}_${months}`;
    } else if (action === 'forward') {
        const days = invoiceData?.duration || 1;
        deepLink = `https://t.me/${botUsername}?start=forward_${domain?.id}_${days}`;
    }

    const starsRate = paymentConfig?.starsRate || 50;
    // Check if Stars payment is allowed for this specific action (e.g. only 'forward')
    const allowedStarsActions = paymentConfig?.starsAllowedActions || ['forward'];
    const isActionAllowedForStars = allowedStarsActions.includes(action) || allowedStarsActions.includes('*') || allowedStarsActions.includes('all');
    const hasStars = paymentConfig ? (paymentConfig.hasStars && isActionAllowedForStars) : isActionAllowedForStars;
    const starsAmount = Math.max(1, Math.round(Number(amount) * starsRate));

    // Direct Telegram Stars invoice deeplink that immediately opens the bill in Telegram
    const starsDirectLink = invoiceData?.starsUrl || (botUsername && invoiceData?.orderRef
        ? `https://t.me/${botUsername}?start=stars_${invoiceData.orderRef}`
        : deepLink);

    const actionTitle = action === 'buy' ? 'Purchase' : action === 'rent' ? 'Rental' : 'Forwarding';

    return (
        <div className="py-1 space-y-4 text-left">
            {/* Header info */}
            <div className="text-center pt-1">
                <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                    {actionTitle}: <span className="text-primary-400 domain-spotlight-text">{domain?.name}</span>
                </h3>
                
                {/* Prominent Order Specs */}
                <div className="mt-2.5 inline-flex flex-wrap items-center justify-center gap-2 px-3.5 py-1.5 rounded-xl bg-neutral-900/90 border border-white/[0.12] text-xs">
                    <span className="font-semibold text-neutral-200">
                        {durationText}
                    </span>
                    {destination && (
                        <>
                            <span className="text-neutral-500">•</span>
                            <span className="text-neutral-400">Target:</span>
                            <span className="font-mono text-primary-300 bg-primary-500/10 px-2 py-0.5 rounded-md border border-primary-500/20 max-w-[240px] truncate" title={destination}>
                                {destination}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Total price card */}
            <div className="bg-gradient-to-b from-neutral-900/90 to-neutral-950/90 rounded-2xl p-4 sm:p-5 border border-white/[0.12] text-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.15)]">
                <p className="text-[11px] sm:text-xs font-semibold text-neutral-400 uppercase tracking-wider">Total Amount</p>
                <div className="flex items-center justify-center gap-2 sm:gap-3 mt-1.5 flex-wrap">
                    <span className="text-2xl sm:text-3xl md:text-4xl font-display font-extrabold text-white">
                        ${amount} USDT
                    </span>
                    {hasStars && (
                        <span className="text-xs font-semibold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                            ≈ {starsAmount} <StarPolygonIcon className="w-3.5 h-3.5 inline text-amber-400" /> Stars
                        </span>
                    )}
                </div>
                <p className="text-[10px] text-neutral-500 mt-1">Instant processing & zero hidden fees</p>
            </div>

            {/* Choose payment method */}
            <div className="border-t border-white/[0.10] pt-3.5">
                <p className="text-xs font-semibold text-neutral-300 uppercase tracking-wider mb-3">
                    {(!hasCryptoBot && !hasDirectWallets && !hasStars) ? 'Complete Your Order:' : 'Choose Payment Method:'}
                </p>

                <div className="space-y-3">
                    {/* Notice when all automated checkout methods are disabled */}
                    {!hasCryptoBot && !hasDirectWallets && !hasStars && (
                        <div className="bg-neutral-900/90 rounded-2xl p-4 border border-amber-500/20 text-center space-y-2.5">
                            <p className="text-xs text-neutral-300 leading-relaxed">
                                Automated online checkout methods are currently offline. To complete the purchase of <strong className="text-white">{domain?.name}</strong>, please contact us directly via our official channels.
                            </p>
                            <div className="p-2.5 rounded-xl bg-neutral-950/80 border border-white/[0.08] text-[11px] text-neutral-400 leading-relaxed">
                                <span className="text-emerald-400 font-semibold">🛡️ Security notice:</span> We never initiate contact first. Always verify official links.
                            </div>
                        </div>
                    )}

                    {/* 1. CryptoBot (if available) */}
                    {hasCryptoBot && (
                        <a
                            href={invoiceData.payUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full bg-sky-600 hover:bg-sky-500 text-white py-3.5 px-4 font-semibold rounded-xl border border-sky-400/30 transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 text-sm"
                        >
                            <CreditCardIcon className="w-4 h-4 text-sky-200" />
                            <span>Pay via CryptoBot (USDT / TON / BTC)</span>
                        </a>
                    )}

                    {/* 2. Direct Crypto Wallet Transfer (if configured) */}
                    {hasDirectWallets && (
                        <div className="bg-neutral-900/70 rounded-2xl p-4 border border-white/[0.10] space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-xs font-semibold text-neutral-300 inline-flex items-center gap-1.5">
                                    <DiamondIcon className="w-3.5 h-3.5 text-cyan-400" />
                                    <span>Direct Crypto Transfer</span>
                                </span>
                                <span className="text-[11px] text-neutral-400">Network:</span>
                            </div>

                            {/* Network tabs: only networks with configured addresses */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                {walletKeys.map(key => {
                                    const net = wallets[key];
                                    const isSelected = selectedNetKey === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => setSelectedNetKey(key)}
                                            className={`py-1.5 px-2 rounded-lg text-xs font-medium transition-all text-center border ${
                                                isSelected
                                                    ? 'bg-white text-neutral-950 font-semibold border-white shadow-sm'
                                                    : 'bg-neutral-950/70 border-white/[0.10] text-neutral-400 hover:text-white hover:border-white/20'
                                            }`}
                                        >
                                            {net.name.split(' ')[0]} ({key.toUpperCase()})
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Selected wallet address box */}
                            {currentWallet && (
                                <div className="bg-neutral-950/80 rounded-xl p-3 border border-white/[0.10] space-y-2 mt-2">
                                    <div className="flex justify-between items-center text-xs">
                                        <span className="text-neutral-400">Send to: <strong className="text-white">{currentWallet.name}</strong></span>
                                        <span className="text-emerald-400 font-medium">${amount} USDT</span>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={currentWallet.address}
                                            className="bg-neutral-900 text-neutral-200 text-xs font-mono rounded-lg px-3 py-2 w-full border border-white/[0.12] focus:outline-none select-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleCopy(currentWallet.address)}
                                            className={`px-3 py-2 text-xs font-semibold rounded-lg transition-all whitespace-nowrap inline-flex items-center gap-1 ${
                                                copied
                                                    ? 'bg-emerald-500 text-white'
                                                    : 'bg-white text-neutral-950 hover:bg-neutral-200 shadow-sm'
                                            }`}
                                        >
                                            {copied ? (
                                                <>
                                                    <CheckIcon className="w-3.5 h-3.5" />
                                                    <span>Copied</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CopyIcon className="w-3.5 h-3.5" />
                                                    <span>Copy</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <p className="text-[10px] text-neutral-500 text-center">
                                        Send exact amount in USDT to this address. Activates automatically.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* 3. Direct Telegram Stars Invoice Button (if Stars enabled for this action) */}
                    {hasStars && (
                        <a
                            href={starsDirectLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white py-3.5 px-4 font-semibold rounded-xl border border-amber-400/30 transition-all shadow-md active:scale-[0.99] flex items-center justify-center gap-2 text-sm"
                        >
                            <StarPolygonIcon className="w-4 h-4 text-amber-200" />
                            <span>Pay with Telegram Stars ({starsAmount} Stars)</span>
                        </a>
                    )}

                    {/* 4. Open in Telegram Bot Menu */}
                    <a
                        href={deepLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-neutral-900 hover:bg-neutral-800 text-neutral-300 hover:text-white py-3 px-4 font-medium rounded-xl border border-white/[0.12] transition-colors flex items-center justify-center gap-2 text-xs"
                    >
                        <RobotIcon className="w-4 h-4 text-primary-400" />
                        <span>Open in Telegram Bot Menu</span>
                    </a>

                </div>
            </div>

            {onBack && (
                <button
                    type="button"
                    onClick={onBack}
                    className="w-full text-center text-neutral-400 hover:text-white text-xs font-medium py-1.5 transition-colors cursor-pointer"
                >
                    ← Back to Order Details
                </button>
            )}
        </div>
    );
};

export default PaymentModalView;


